<template>
    <BModal
        :model-value="modal"
        scrollable
        centered
        @update:modelValue="updateModal"
        no-close-on-backdrop
    >
        <template #header>
            <div
                class="d-flex align-items-center justify-content-between modal-title w-100"
            >
                <h5 class="">Payment Method</h5>
                <BButton
                    variant="link"
                    class="close"
                    @click="updateModal(false)"
                >
                    <i class="ion ion-android-close"></i>
                </BButton>
            </div>
        </template>

        <BForm @submit.stop.prevent>
            <BRow>
                <BCol md="12">
                    <BFormGroup
                        label="Type *"
                        label-for="type"
                        class="text-start mb-3"
                    >
                        <BFormInput
                            v-model="form.type"
                            type="text"
                            id="type"
                            placeholder="Enter Type (e.g. Bank Transfer, Cash)"
                            :class="{
                                'is-invalid':
                                    v$.form.type.$dirty &&
                                    v$.form.type.$invalid,
                            }"
                        />
                        <BFormInvalidFeedback
                            :state="
                                v$.form.type.$dirty && v$.form.type.$invalid
                            "
                        >
                            {{ v$.form.type.$errors[0]?.$message }}
                        </BFormInvalidFeedback>
                    </BFormGroup>
                </BCol>
                <BCol md="12">
                    <BFormGroup
                        label="Bank Name *"
                        label-for="name"
                        class="text-start mb-3"
                    >
                        <BFormInput
                            v-model="form.name"
                            type="text"
                            id="name"
                            placeholder="Enter Name (e.g. Bank Mandiri)"
                            :class="{
                                'is-invalid':
                                    v$.form.name.$dirty &&
                                    v$.form.name.$invalid,
                            }"
                        />
                        <BFormInvalidFeedback
                            :state="
                                v$.form.name.$dirty && v$.form.name.$invalid
                            "
                        >
                            {{ v$.form.name.$errors[0]?.$message }}
                        </BFormInvalidFeedback>
                    </BFormGroup>
                </BCol>
                <BCol md="12">
                    <BFormGroup
                        label="Account Name"
                        label-for="owned_by"
                        class="text-start mb-3"
                    >
                        <BFormInput
                            v-model="form.owned_by"
                            type="text"
                            id="owned_by"
                            placeholder="Enter Account Name"
                        />
                    </BFormGroup>
                </BCol>
                <BCol md="12">
                    <BFormGroup
                        label="Account Number"
                        label-for="number"
                        class="text-start mb-3"
                    >
                        <BFormInput
                            v-model="form.number"
                            type="text"
                            id="number"
                            placeholder="Enter Account Number"
                        />
                    </BFormGroup>
                </BCol>
                <BCol md="12">
                    <BFormGroup
                        label="SWIFT Code"
                        label-for="swift_code"
                        class="text-start mb-3"
                    >
                        <BFormInput
                            v-model="form.swift_code"
                            type="text"
                            id="swift_code"
                            placeholder="Enter SWIFT Code"
                        />
                    </BFormGroup>
                </BCol>
                <BCol md="12">
                    <BFormGroup
                        label="Bank Address"
                        label-for="bank_address"
                        class="text-start mb-3"
                    >
                        <BFormTextarea
                            v-model="form.bank_address"
                            id="bank_address"
                            placeholder="Enter Bank Address"
                            rows="3"
                        />
                    </BFormGroup>
                </BCol>
                <BCol md="12">
                     <BFormGroup
                        label="Status *"
                        label-for="status"
                        class="text-start mb-3"
                    >
                        <v-select
                            v-model="form.status"
                            :options="[
                                { label: 'Active', value: 'active' },
                                { label: 'Inactive', value: 'inactive' },
                            ]"
                            :reduce="(option) => option.value"
                            placeholder="Select Status"
                            :class="{
                                'is-invalid':
                                    v$.form.status.$dirty &&
                                    v$.form.status.$invalid,
                            }"
                        />
                        <BFormInvalidFeedback
                            :state="
                                v$.form.status.$dirty && v$.form.status.$invalid
                            "
                        >
                            {{ v$.form.status.$errors[0]?.$message }}
                        </BFormInvalidFeedback>
                    </BFormGroup>
                </BCol>
            </BRow>
        </BForm>

        <template #footer>
            <div class="d-flex justify-content-end">
                <BButton variant="primary" class="me-2" @click="handleAction"
                    >Save</BButton
                >
                <BButton variant="danger" @click="updateModal(false)"
                    >Cancel</BButton
                >
            </div>
        </template>
    </BModal>
</template>

<script setup>
import axios from "axios";
import { defineProps, defineEmits, reactive, watch } from "vue";
import {
    BModal,
    BRow,
    BCol,
    BForm,
    BFormInput,
    BButton,
    BFormGroup,
    BFormInvalidFeedback,
    BFormTextarea,
} from "bootstrap-vue-next";
import { useVuelidate } from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import { useLoading } from "vue-loading-overlay";
import { useAuthStore } from "@/store/auth.js";
import { useToast } from "vue-toastification";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";

const auth = useAuthStore();
const $loading = useLoading();
const toast = useToast();

const props = defineProps({
    modal: Boolean,
    selectedData: Object,
});

const emit = defineEmits(["update:modal", "addOrUpdate"]);

const updateModal = (value) => {
    emit("update:modal", value);
};

const resetForm = () => {
    Object.assign(form, { ...formData });
    v$.value.$reset();
};

const formData = {
    type: "",
    name: "",
    owned_by: "",
    number: "",
    swift_code: "",
    bank_address: "",
    status: "active",
};

const form = reactive({ ...formData });

watch(
    () => props.selectedData,
    (newData) => {
        if (newData && Object.keys(newData).length > 0) {
            Object.assign(form, { ...newData });
        } else {
            Object.assign(form, { ...formData });
        }
    },
    { immediate: true }
);

const rules = {
    form: {
        type: { required: helpers.withMessage("Type is required", required) },
        name: { required: helpers.withMessage("Bank Name is required", required) },
        status: { required: helpers.withMessage("Status is required", required) },
    },
};

const v$ = useVuelidate(rules, { form });

const handleAction = () => {
    props.selectedData.uuid ? update() : create();
};

const create = async () => {
    v$.value.$touch();
    if (!v$.value.$invalid) {
        const loader = $loading.show({ color: "#489464", loader: "spinner" });
        try {
            const response = await axios.post(
                `${process.env.VUE_APP_API_URL}/payment-method/add`,
                form,
                {
                    headers: {
                        "Content-type": "application/json",
                        Authorization: "Bearer " + auth._token,
                    },
                }
            );

            if (response.data.status) {
                emit("addOrUpdate");
                toast.success(response.data.message);
                updateModal(false);
                resetForm();
            } else {
                toast.error(response.data.message);
            }
        } finally {
            loader.hide();
        }
    }
};

const update = async () => {
    v$.value.$touch();
    if (!v$.value.$invalid) {
        const loader = $loading.show({ color: "#489464", loader: "spinner" });
        try {
            const response = await axios.put(
                `${process.env.VUE_APP_API_URL}/payment-method/edit/${props.selectedData.uuid}`,
                form,
                {
                    headers: {
                        "Content-type": "application/json",
                        Authorization: "Bearer " + auth._token,
                    },
                }
            );

            if (response.data.status) {
                emit("addOrUpdate");
                toast.success(response.data.message);
                updateModal(false);
                resetForm();
            } else {
                toast.error(response.data.message);
            }
        } finally {
            loader.hide();
        }
    }
};
</script>
